import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { PropertyInfoResponse } from "@/features/propertyCodeAcquisition/types";

/** 未取得物件の地番マスク（伏せ字）。 */
const CHIBAN_MASK = "●●●●●";

interface PropertyInfoHeaderProps {
  propertyInfo: PropertyInfoResponse;
}

/** ラベルと値の 1 行（縦並びの定義リスト項目）。pptx slide4 の `ラベル： 値` 書式。 */
const InfoRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: "flex", gap: 1 }}>
    <Typography component="dt" sx={{ minWidth: 96 }}>
      {label}：
    </Typography>
    <Typography component="dd" sx={{ m: 0 }}>
      {value}
    </Typography>
  </Box>
);

/**
 * 物件情報ヘッダー（物件情報の定義リストのみ）。
 * usePropertyInfo が返す PropertyInfoResponse を受け取り、
 * 不動産種別 / 都道府県 / 市区町村 / 大字・丁目 / 地番 を `ラベル： 値` の縦並びで表示する（pptx slide2/4）。
 * 地番ラベルは土地のみ。建物・区分建物では家屋番号と表示する。
 * ページタイトル（不動産登記取得履歴）は他画面と統一するためルート側（PageTitle）に置く。
 * 未取得（is_acquired=false・chiban_or_kaoku=null）の地番は伏せ字でマスクし、
 * 生の地番は DOM に出さない。
 */
const PropertyInfoHeaderComponent: React.FC<PropertyInfoHeaderProps> = ({
  propertyInfo,
}) => {
  // 不動産種別はバックエンド返却の表示名文字列をそのまま表示する。
  const {
    realEstateType,
    prefectureName,
    cityName,
    ooaza,
    chibanOrKaoku,
    isAcquired,
  } = propertyInfo;

  // 取得済みのみ地番・家屋番号を表示。未取得はマスク値のみをレンダリングする。
  // 取得済みでも地番が未確定（null）のときは空欄ではなく「—」を表示する。
  const chibanDisplay = isAcquired ? chibanOrKaoku ?? "—" : CHIBAN_MASK;

  // 土地は地番、建物・区分建物は家屋番号としてラベルを切り替える。
  const chibanLabel = realEstateType === "土地" ? "地番" : "家屋番号";

  return (
    <Stack component="dl" spacing={0.5} sx={{ m: 0 }}>
      <InfoRow label="不動産種別" value={realEstateType} />
      <InfoRow label="都道府県" value={prefectureName} />
      <InfoRow label="市区町村" value={cityName} />
      <InfoRow label="大字・丁目" value={ooaza ?? ""} />
      <InfoRow label={chibanLabel} value={chibanDisplay} />
    </Stack>
  );
};

/**
 * 取得フロー中のダイアログ開閉・送信中フラグ・historyResetSignal の state 変化（ルート再レンダリング）で、
 * 内容の変わらない物件情報ヘッダーが再描画されるのを防ぐ。propertyInfo は安定参照のためメモ化が効く。
 */
export const PropertyInfoHeader = React.memo(PropertyInfoHeaderComponent);
